
import Button from '@mui/material/Button'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import LoadingOverlay from '../LoadingOverlay'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { getQueuePostAction, handleMultiPostError, setComposerBulkUploadFileUrl } from 'services/compose'
import { createPost, getPostData } from 'services/compose/create/createPost'
import { createMultiPost } from 'services/compose/multi/create'
import {
  composerBulkFileSelector,
  composerPinterestPostStatusSelector,
  composerSelectedBucketIdSelector,
  composerSelectedProfilesSelector,
  multiPostsCountSelector,
  multiPostsUploadingSelector,
  PINTEREST_BOARD_NOT_SELECTED_MESSAGE_KEY
} from 'services/compose/selectors'
import { enforcePlannedPostsLimit } from 'services/product'
import { message, warning } from 'services/snackbar'
import { CreatePostRequestData } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { BULK_UPLOAD_ERROR_MESSAGE, BULK_UPLOAD_ERROR_MESSAGE_TIMEOUT } from 'utils/composer'
import toCamelCase from 'utils/toCamelCase'
import AddToBucketButton from './AddToBucketButton'
import styles from './BulkActions.pcss'
import ComposerActionsButton from './ComposerActionsButton'
import { useSubmitShortcut } from './hooks/useSubmitShortcut'
import { QueuePostsActionSelect } from './QueuePostsActionSelect'

function BulkActions(props: { onMultiPostCreated: (redirectUrl?: string) => void, onBulkUploadStarted?: (redirectUrl?: string) => void }) {
  const { onMultiPostCreated, onBulkUploadStarted } = props
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const selectedProfiles = useSelector(composerSelectedProfilesSelector)
  const bulkUploadFile = useSelector(composerBulkFileSelector)
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const multiPostsUploading = useSelector(multiPostsUploadingSelector)
  const selectedBucketId = useSelector(composerSelectedBucketIdSelector)
  const pinterestPostStatus = useSelector(composerPinterestPostStatusSelector)
  const [loading, setLoading] = useState(false)
  const queuePostsAction = useSelector(getQueuePostAction)

  const createMulti$ = useRef<Subject<'queue' | 'next'>>(undefined)
  const createBulk$ = useRef<Subject<{ data: CreatePostRequestData, postsCount: number }>>(undefined)

  useEffect(() => {
    createBulk$.current = new Subject()
    createBulk$.current
      .pipe(
        mergeMap(({ data, postsCount }) => {
          if (onBulkUploadStarted) {
            const redirectUrl = selectedBucketId ? `/posts/buckets/${selectedBucketId}/posts` : '/posts/planned/all'
            onBulkUploadStarted(redirectUrl)
          }
          return dispatch(createPost(data, selectedBucketId)).pipe(catchError(error => {
            if (!error.response.error) {
              // EXPL: There's no error data, show generic error message
              dispatch(message(intl.formatMessage({ id: 'errors.create-post' }), 'error'))
              return observableOf({ error: true, handled: true })
            }
            const errorData = toCamelCase(error.response.error)
            return observableOf({ error: errorData, postData: data, postsCount } as any)
          }))
        })
      )
      .subscribe((response: any) => {
        dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
        if (response.handled) {
          return
        }

        if (response.error) {
          const { message: msg, messageKey, data, bulkUploadResultsUrl } = response.error
          if (bulkUploadResultsUrl) {
            dispatch(warning(
              BULK_UPLOAD_ERROR_MESSAGE,
              'download failed posts',
              () => { window.open(bulkUploadResultsUrl, '_self') },
              BULK_UPLOAD_ERROR_MESSAGE_TIMEOUT
            ))
            return
          }

          // EXPL: For bulk uploads the real post count is rows * profiles, not the profile count (ppPageIds)
          const newPostsCount = response.postsCount ?? response.postData.ppPageIds.length
          const nextPlannedPostsCount = data.plannedPostsCount + newPostsCount

          if (nextPlannedPostsCount > data.plannedPostsLimit) {
            dispatch(enforcePlannedPostsLimit(
              nextPlannedPostsCount,
              intl.formatMessage({ id: 'composer.notifications.planned-limit-reached' }),
              'warning'
            ))
          } else {
            dispatch(message(messageKey ? intl.formatMessage({ id: messageKey, defaultMessage: msg }) : msg, 'error'))
          }
          return
        }
        dispatch(message(intl.formatMessage({ id: 'notifications.post-added-to-plan' }), 'success'))
      })

    return () => {
      // Complete source stream so active create requests can finish after composer unmount.
      createBulk$.current?.complete()
    }
  }, [dispatch, selectedBucketId, intl, onBulkUploadStarted])

  useEffect(() => {
    createMulti$.current = new Subject()
    createMulti$.current
      .pipe(
        mergeMap((shareType) => {
          setLoading(true)
          dispatch(message(intl.formatMessage({ id: 'composer.notifications.create-post-started' }), 'info'))
          return dispatch(createMultiPost(shareType))
            .pipe(catchError(error => {
              if (!error.response.error) {
                // EXPL: There's no error data, show generic error message
                dispatch(message(intl.formatMessage({ id: 'errors.create-post' }), 'error'))
                return observableOf({ error: true, handled: true })
              }
              const errorData = toCamelCase(error.response.error)
              return observableOf({ error: errorData } as any)
            }))
        })
      )
      .subscribe(response => {
        setLoading(false)
        if (response.posts?.length > 0) {
          if (response.downloadReport) {
            dispatch(warning(
              intl.formatMessage({ id: 'composer.notifications.multipost-failed' }),
              intl.formatMessage({ id: 'composer.notifications.multipost-get-error-report' }),
              () => { window.open(response.downloadReport, '_self') },
              BULK_UPLOAD_ERROR_MESSAGE_TIMEOUT
            ))
          }

          dispatch(message(intl.formatMessage({ id: 'composer.notifications.multipost-failed' }), 'warning'))
          dispatch(handleMultiPostError(response.posts))
        } else if (response.error) {
          const { message: msg, messageKey, data } = response.error
          const nextPlannedPostsCount = data.plannedPostsCount + multiPostsCount

          if (nextPlannedPostsCount > data.plannedPostsLimit) {
            dispatch(enforcePlannedPostsLimit(
              nextPlannedPostsCount,
              intl.formatMessage({ id: 'composer.notifications.planned-limit-reached' }),
              'warning'
            ))
          } else {
            dispatch(message(messageKey ? intl.formatMessage({ id: messageKey, defaultMessage: msg }) : msg, 'error'))
          }
        } else {
          const redirectUrl = selectedBucketId ? `/posts/buckets/${selectedBucketId}/posts` : '/posts/planned/all'
          onMultiPostCreated(redirectUrl)
          dispatch(message(intl.formatMessage({ id: 'notifications.multipost-added' }), 'success'))
        }
      })

    return () => {
      createMulti$.current?.unsubscribe()
    }
  }, [dispatch, intl, onMultiPostCreated, multiPostsCount, selectedBucketId])

  const queueMultiPosts = () => {
    createMulti$.current?.next(queuePostsAction === 'top' ? 'next' : 'queue')
  }

  const queueBulkPosts = useCallback(() => {
    const data = dispatch(getPostData(queuePostsAction === 'top' ? 'next' : 'queue'))
    const postsCount = bulkUploadFile ? bulkUploadFile.rows * selectedProfiles.length : data.ppPageIds.length
    createBulk$.current?.next({ data, postsCount })
  }, [dispatch, queuePostsAction, bulkUploadFile, selectedProfiles])

  const withError = pinterestPostStatus.caption.errors.includes(PINTEREST_BOARD_NOT_SELECTED_MESSAGE_KEY)

  const submitByShortcut = useCallback(() => {
    if (bulkUploadFile) {
      queueBulkPosts()
      return
    }
    if (!loading && multiPostsCount > 0) {
      createMulti$.current?.next('queue')
    }
  }, [bulkUploadFile, loading, multiPostsCount, queueBulkPosts])

  useSubmitShortcut({
    enabled: true,
    isReady: !bulkUploadFile || !withError,
    notReadyMessage: 'There\'s an issue with your post. Check the network options in Step 3.',
    onSubmit: submitByShortcut
  })

  const onAddToBucket = () => {
    createMulti$.current?.next(queuePostsAction === 'top' ? 'next' : 'queue')
  }

  const bulkUploadPostsCount = bulkUploadFile ? bulkUploadFile.rows * selectedProfiles.length : 0

  return (
    <div className={styles.container} data-testid="bulk-actions">
      <QueuePostsActionSelect disabled={loading || multiPostsUploading} />
      {bulkUploadFile && (
        <Fragment>
          {selectedBucketId && (
            <Button
              variant="contained"
              color="primary"
              disabled={withError}
              className={styles.btn}
              onClick={queueBulkPosts}
            >
              <FormattedMessage
                id="composer.actions.bulk-upload-bucket"
                values={{ posts: bulkUploadPostsCount }}
              />
            </Button>
          )}
          {!selectedBucketId && (
            <Button
              variant="contained"
              color="primary"
              disabled={withError}
              className={styles.btn}
              onClick={queueBulkPosts}
            >
              <FormattedMessage
                id="composer.actions.bulk-upload-queue"
                values={{ count: bulkUploadPostsCount }}
              />
            </Button>
          )}
        </Fragment>
      )}
      <div className={styles['multi-posts-actions']}>
        {multiPostsCount > 0 && !selectedBucketId && (
          <ComposerActionsButton
            disabled={loading || multiPostsUploading}
            postsCount={multiPostsCount}
            onAddToPlan={queueMultiPosts}
          />
        )}
        {multiPostsCount > 0 && selectedBucketId && (
          <AddToBucketButton
            disabled={loading || multiPostsUploading}
            postsCount={multiPostsCount}
            className={styles.btn}
            onSubmit={onAddToBucket}
          />
        )}
      </div>
      <LoadingOverlay open={loading} message="Creating posts, please wait..." />
    </div>
  )
}

export default BulkActions
